import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  LinearProgress,
  IconButton,
  Alert,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Delete,
  Download,
} from '@mui/icons-material';
import api from '../api';

/**
 * Professional CV Upload Component
 * Supports drag-and-drop, file validation, and progress tracking
 */
const CVUpload = ({ candidateId, existingCV, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  const maxSize = 5 * 1024 * 1024; // 5MB

  const validateFile = (file) => {
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload PDF, DOC, or DOCX files only.');
      return false;
    }

    if (file.size > maxSize) {
      setError('File size exceeds 5MB limit.');
      return false;
    }

    return true;
  };

  const handleFileSelect = (file) => {
    setError('');
    setSuccess('');

    if (validateFile(file)) {
      setSelectedFile(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('cv', selectedFile);

    try {
      const response = await api.post('/candidate/upload-cv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      if (response.data.success) {
        setSuccess('CV uploaded successfully!');
        setSelectedFile(null);
        if (onUploadSuccess) {
          onUploadSuccess(response.data.data);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload CV');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async () => {
    if (!existingCV) return;

    try {
      const response = await api.post('/candidate/delete-file', {
        path: existingCV.path,
        type: 'cv',
      });

      if (response.data.success) {
        setSuccess('CV deleted successfully');
        if (onUploadSuccess) {
          onUploadSuccess(null);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete CV');
    }
  };

  const handleDownload = () => {
    if (existingCV?.url) {
      window.open(existingCV.url, '_blank');
    }
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1.5,
          color: 'text.secondary',
          fontWeight: 500,
          fontSize: '0.875rem',
        }}
      >
        Upload CV (PDF, DOC, DOCX - Max 5MB)
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {existingCV ? (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '2px solid',
            borderColor: 'success.main',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Description sx={{ fontSize: 40, color: 'success.main' }} />
            <Box>
              <Typography variant="body1" fontWeight={600}>
                {existingCV.original_name || 'CV.pdf'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Uploaded successfully
              </Typography>
            </Box>
          </Box>
          <Box>
            <IconButton onClick={handleDownload} color="primary">
              <Download />
            </IconButton>
            <IconButton onClick={handleDelete} color="error">
              <Delete />
            </IconButton>
          </Box>
        </Paper>
      ) : (
        <>
          <Paper
            elevation={0}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            sx={{
              p: 4,
              border: '2px dashed',
              borderColor: dragActive ? 'primary.main' : 'divider',
              borderRadius: 2,
              backgroundColor: dragActive ? 'action.hover' : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.3s',
              textAlign: 'center',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />

            <CloudUpload
              sx={{
                fontSize: 60,
                color: dragActive ? 'primary.main' : 'text.secondary',
                mb: 2,
              }}
            />

            <Typography variant="h6" gutterBottom>
              {selectedFile ? selectedFile.name : 'Drop your CV here'}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              or click to browse files
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Supported formats: PDF, DOC, DOCX (Max 5MB)
            </Typography>
          </Paper>

          {selectedFile && (
            <Box sx={{ mt: 2 }}>
              {uploading && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}
                  >
                    Uploading... {uploadProgress}%
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={uploading}
                  fullWidth
                  startIcon={<CloudUpload />}
                >
                  {uploading ? 'Uploading...' : 'Upload CV'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setSelectedFile(null)}
                  disabled={uploading}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CVUpload;
